import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { PhotoResult, PhotoWindowInfo } from '../../shared/protocol';
import { formatCountdown } from '../util/format';
import { useServerClock } from '../util/clock';
import { useGame } from '../state/SocketProvider';

/** Received photos (both roles) + a seeker "awaiting photo" indicator. The
 *  seeker reads the image as a clue; the hider sees what they sent. Tapping a
 *  thumbnail opens an on-brand lightbox. Carries no location (§5). */
export function PhotoViewer({
  photos,
  windows,
  serverNow,
  isSeeker,
}: {
  photos: PhotoResult[];
  windows: PhotoWindowInfo[];
  serverNow: number;
  isSeeker: boolean;
}) {
  const now = useServerClock(
    serverNow,
    windows.map((w) => w.endsAt),
  );
  const { send } = useGame();
  const [open, setOpen] = useState<PhotoResult | null>(null);
  const [reported, setReported] = useState(false);
  const showAwait = isSeeker && windows.length > 0;
  if (photos.length === 0 && !showAwait) return null;

  return (
    <div className="photo-strip" data-testid="photo-strip">
      {showAwait &&
        windows
          .slice()
          .sort((a, b) => a.endsAt - b.endsAt)
          .map((w) => (
            <div className="photo-await" key={w.cardId} data-testid="photo-awaiting">
              <span className="photo-await__label">{w.label}</span>
              <span className="photo-await__tag">Awaiting photo</span>
              <span className="photo-await__count">{formatCountdown(Math.max(0, w.endsAt - now()))}</span>
            </div>
          ))}
      {photos
        .slice()
        .reverse()
        .map((p) => (
          <button
            className="photo-thumb"
            key={p.id}
            data-testid={`photo-thumb-${p.cardId}`}
            onClick={() => setOpen(p)}
          >
            <img src={p.url} alt={p.label} loading="lazy" />
            <span className="photo-thumb__label">{p.label}</span>
          </button>
        ))}

      {open &&
        createPortal(
          // Portalled to <body> so the fixed overlay escapes the HUD's stacking
          // context (z-index) — otherwise the card rail renders over it.
          <div className="photo-light" data-testid="photo-light" onClick={() => setOpen(null)}>
            <div className="photo-light__inner" onClick={(e) => e.stopPropagation()}>
              <img src={open.url} alt={open.label} />
              <div className="photo-light__bar">
                <span className="photo-light__cap">{open.label}</span>
                {/* v19 BOLT-094 (Guideline 1.2): report objectionable player media. */}
                {reported ? (
                  <span className="photo-light__cap" data-testid="photo-reported">
                    Reported
                  </span>
                ) : (
                  <button
                    className="btn btn--ghost"
                    data-testid="photo-report"
                    onClick={() => {
                      send({ t: 'report', reason: 'objectionable', mediaId: open.id, idem: `rep-${open.id}` });
                      setReported(true);
                    }}
                  >
                    Report
                  </button>
                )}
                <button
                  className="btn btn--ghost"
                  data-testid="photo-report-leave"
                  onClick={() => send({ t: 'report', reason: 'abusive', mediaId: open.id, block: true, idem: `rep-block-${open.id}` })}
                >
                  Report &amp; leave
                </button>
                <button className="btn" data-testid="photo-light-close" onClick={() => setOpen(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
